import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";
import { TableUIPreferences } from "@/shared/domain/config/ConfigRecords";

import {
  getQueryKey,
  SearchTablePreferenceKey,
} from "./useSearchTablePreferences";

/**
 * Generic hook to update search table preferences for a specific config key.
 * Handles updating table preferences and invalidating the query cache.
 *
 * @param configKey - The config key identifying which table preferences to update
 * @returns React Query mutation result for updating preferences
 */
export function useUpdateSearchTablePreferences(
  configKey: SearchTablePreferenceKey,
) {
  const queryClient = useQueryClient();
  const { configService } = useDomainServices();

  return useMutation({
    mutationFn: async ({
      orgId,
      preferences,
    }: {
      orgId: string;
      preferences: Partial<TableUIPreferences>;
    }) => {
      // Backend handles merging
      return configService.updateTablePreferences(
        orgId,
        configKey,
        preferences,
      );
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: getQueryKey(configKey),
      });
    },
  });
}
